import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export interface Quay {
  id: string;
  name: string;
  marinaId: string;
  clientId?: string; // Required for create/update
  latitude?: number;
  longitude?: number;
  marinaLayoutObjectId?: string; // Reference to layout object
  status?: 'VACANT' | 'OCCUPIED' | 'RESERVED' | 'OUT_OF_SERVICE';
  berths?: Berth[];
  boatRestrictions?: BoatRestrictions;
  createdAt?: string;
  updatedAt?: string;
  marinaName?: string; // Read-only from API
}

export interface Berth {
  id: string;
  name: string; // Required by API
  latitude?: number;
  longitude?: number;
  quayId: string;
  quayName?: string; // Name of the quay this berth belongs to
  marinaId?: string;
  marinaName?: string; // Name of the marina
  clientId?: string; // Client UUID - Required for create/update
  position?: number;
  status?: 'VACANT' | 'OCCUPIED' | 'RESERVED' | 'OUT_OF_SERVICE';
  layoutObjectId?: string;
  marinaLayoutObjectId?: string;
  mooringType?: 'ALONGSIDE' | 'BOOM_ON_SIDE' | 'SINGLE_BUOY' | 'ANCHOR_STERN_IN' | 'SLIME_LINES';
  currentBooking?: Booking;
  boatRestrictions?: BoatRestrictions;
  utilities?: {
    water?: boolean;
    electricity?: boolean;
    wifi?: boolean;
    cable?: boolean;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface Buoy {
  id: string;
  name: string; // API uses 'name', not 'number'
  number?: string; // Keep for backward compatibility/display
  marinaId: string;
  marinaName?: string; // Read-only from API
  berthId?: string; // Optional link to berth
  berthName?: string; // Read-only from API
  quayId?: string; // Optional link to quay
  quayName?: string; // Read-only from API
  clientId?: string; // Required for create/update
  latitude?: number;
  longitude?: number;
  status?: 'VACANT' | 'OCCUPIED' | 'RESERVED' | 'OUT_OF_SERVICE';
  layoutObjectId?: string;
  marinaLayoutObjectId?: string; // Reference to layout object (correct field name)
  currentBooking?: Booking;
  boatRestrictions?: BoatRestrictions;
  createdAt?: string;
  updatedAt?: string;
}

export interface Booking {
  id: string;
  boatId: string;
  boatName: string;
  customerId: string;
  customerName: string;
  startDate: string;
  endDate?: string;
  status: 'active' | 'reserved' | 'completed' | 'cancelled';
}

export interface BoatRestrictions {
  maxBoatDraft?: number;
  maxBoatLength?: number;
  maxBoatWidth?: number;
  maxBoatDisplacement?: number;
}

export interface LayoutObject {
  id: string;
  fabricObjectId?: string;
  type: 'berth' | 'quay' | 'buoy' | 'facility' | 'decoration';
  entityId?: string;
  position: {
    x: number;
    y: number;
    width?: number;
    height?: number;
    rotation?: number;
  };
  style: {
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    opacity?: number;
  };
  label?: string;
  metadata?: any;
}

export interface MarinaLayout {
  id?: string;
  marinaId: string;
  clientId: string;
  name?: string;
  description?: string;
  version?: number;
  layout: any; // Fabric.js JSON
  isActive?: boolean;
  canvasWidth?: number;
  canvasHeight?: number;
  backgroundColor?: string;
  objects?: LayoutObject[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUpdateMarinaLayoutDto {
  marinaId: string;
  clientId: string;
  name?: string;
  description?: string;
  version?: number;
  layout: any; // Fabric.js JSON
  isActive?: boolean;
  canvasWidth?: number;
  canvasHeight?: number;
}

@Injectable({
  providedIn: 'root'
})
export class QuayService {
  private apiUrl = '/api/v1/quays';
  
  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[QuayService] Service initialized with WebSocket support');
  }

  /**
   * Get all quays for a marina
   */
  getQuaysByMarinaId(marinaId: string): Observable<Quay[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching quays for marina:', marinaId);
    
    return this.http.get<Quay[]>(`${this.apiUrl}/marina/${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching quays:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get quay by ID
   */
  getQuayById(id: string): Observable<Quay> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Quay>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching quay by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new quay
   */
  createQuay(quay: Omit<Quay, 'id'>): Observable<Quay> {
    const headers = this.getAuthHeaders();
    console.log('Creating quay:', quay);
    
    return this.http.post<Quay>(this.apiUrl, quay, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating quay:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing quay (full update)
   */
  updateQuay(id: string, quay: Partial<Quay>): Observable<Quay> {
    const headers = this.getAuthHeaders();
    console.log('Updating quay:', id, quay);
    
    return this.http.put<Quay>(`${this.apiUrl}/${id}`, quay, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating quay:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Partially update a quay (for single field updates like layoutObjectId)
   */
  patchQuay(id: string, updates: Partial<Quay>): Observable<Quay> {
    const headers = this.getAuthHeaders();
    console.log('Patching quay:', id, updates);
    
    return this.http.patch<Quay>(`${this.apiUrl}/${id}`, updates, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error patching quay:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a quay
   */
  deleteQuay(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    console.log('Deleting quay:', id);
    
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting quay:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update a berth (full update)
   */
  updateBerth(id: string, berth: Partial<Berth>): Observable<Berth> {
    const headers = this.getAuthHeaders();
    console.log('Updating berth:', id, berth);
    
    return this.http.put<Berth>(`/api/v1/berths/${id}`, berth, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating berth:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Partially update a berth (for single field updates like layoutObjectId)
   */
  patchBerth(id: string, updates: Partial<Berth>): Observable<Berth> {
    const headers = this.getAuthHeaders();
    console.log('Patching berth:', id, updates);
    
    return this.http.patch<Berth>(`/api/v1/berths/${id}`, updates, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error patching berth:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update a buoy (full update)
   */
  updateBuoy(id: string, buoy: Partial<Buoy>): Observable<Buoy> {
    const headers = this.getAuthHeaders();
    console.log('Updating buoy:', id, buoy);
    
    return this.http.put<Buoy>(`/api/v1/buoys/${id}`, buoy, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating buoy:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Partially update a buoy (for single field updates like layoutObjectId)
   */
  patchBuoy(id: string, updates: Partial<Buoy>): Observable<Buoy> {
    const headers = this.getAuthHeaders();
    console.log('Patching buoy:', id, updates);
    
    return this.http.patch<Buoy>(`/api/v1/buoys/${id}`, updates, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error patching buoy:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all quays
   */
  getAllQuays(): Observable<Quay[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching all quays');
    
    return this.http.get<Quay[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching all quays:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all berths for a marina
   */
  getBerthsByMarinaId(marinaId: string): Observable<Berth[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching berths for marina:', marinaId);
    
    return this.http.get<Berth[]>(`/api/v1/berths?marinaId=${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching berths:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all berths
   */
  getAllBerths(): Observable<Berth[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching all berths');
    
    return this.http.get<Berth[]>(`/api/v1/berths`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching all berths:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get berth by ID
   */
  getBerthById(id: string): Observable<Berth> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Berth>(`/api/v1/berths/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching berth by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new berth
   */
  createBerth(berth: Omit<Berth, 'id'>): Observable<Berth> {
    const headers = this.getAuthHeaders();
    console.log('Creating berth:', berth);
    
    return this.http.post<Berth>(`/api/v1/berths`, berth, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating berth:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a berth
   */
  deleteBerth(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    console.log('Deleting berth:', id);
    
    return this.http.delete<void>(`/api/v1/berths/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting berth:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all buoys for a marina
   */
  getBuoysByMarinaId(marinaId: string): Observable<Buoy[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching buoys for marina:', marinaId);
    
    return this.http.get<Buoy[]>(`/api/v1/buoys?marinaId=${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching buoys:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all buoys
   */
  getAllBuoys(): Observable<Buoy[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching all buoys');
    
    return this.http.get<Buoy[]>(`/api/v1/buoys`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching all buoys:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get buoy by ID
   */
  getBuoyById(id: string): Observable<Buoy> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Buoy>(`/api/v1/buoys/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching buoy by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new buoy
   */
  createBuoy(buoy: Omit<Buoy, 'id'>): Observable<Buoy> {
    const headers = this.getAuthHeaders();
    console.log('Creating buoy:', buoy);
    
    return this.http.post<Buoy>(`/api/v1/buoys`, buoy, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating buoy:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a buoy
   */
  deleteBuoy(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    console.log('Deleting buoy:', id);
    
    return this.http.delete<void>(`/api/v1/buoys/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting buoy:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new marina layout (Fabric.js canvas JSON)
   */
  createMarinaLayout(layout: CreateUpdateMarinaLayoutDto): Observable<MarinaLayout> {
    const headers = this.getAuthHeaders();
    console.log('Creating marina layout:', layout);
    
    return this.http.post<MarinaLayout>('/api/marina-layouts', layout, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating marina layout:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing marina layout (Fabric.js canvas JSON)
   */
  updateMarinaLayout(layoutId: string, layout: CreateUpdateMarinaLayoutDto): Observable<MarinaLayout> {
    const headers = this.getAuthHeaders();
    console.log('Updating marina layout:', layoutId, layout);
    
    return this.http.put<MarinaLayout>(`/api/marina-layouts/${layoutId}`, layout, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating marina layout:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get latest marina layout for a marina (Fabric.js canvas JSON)
   */
  getMarinaLayoutLatest(marinaId: string): Observable<MarinaLayout> {
    const headers = this.getAuthHeaders();
    console.log('Fetching latest marina layout for marina:', marinaId);
    
    return this.http.get<MarinaLayout>(`/api/marina-layouts/marina/${marinaId}/latest`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching latest marina layout:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get active marina layout for a marina (Fabric.js canvas JSON)
   */
  getMarinaLayoutActive(marinaId: string): Observable<MarinaLayout> {
    const headers = this.getAuthHeaders();
    console.log('Fetching active marina layout for marina:', marinaId);
    
    return this.http.get<MarinaLayout>(`/api/marina-layouts/marina/${marinaId}/active`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching active marina layout:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all marina layouts for a marina
   */
  getMarinaLayouts(marinaId: string): Observable<MarinaLayout[]> {
    const headers = this.getAuthHeaders();
    console.log('Fetching marina layouts for marina:', marinaId);
    
    return this.http.get<MarinaLayout[]>(`/api/marina-layouts/marina/${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching marina layouts:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }

  /**
   * WebSocket Integration Methods
   */

  /**
   * Get observable for real-time quay updates from WebSocket (create/update)
   */
  getRealtimeQuayUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Quay`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time quay deletions from WebSocket
   */
  getRealtimeQuayDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Quay`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time berth updates from WebSocket (create/update)
   */
  getRealtimeBerthUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Berth`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time berth deletions from WebSocket
   */
  getRealtimeBerthDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Berth`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time buoy updates from WebSocket (create/update)
   */
  getRealtimeBuoyUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Buoy`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time buoy deletions from WebSocket
   */
  getRealtimeBuoyDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Buoy`)
    );
    return merge(...observables);
  }
}
